import React, { useState, useEffect } from 'react';
import { useUser } from "../providers/user";
import googleSheetsService from '../services/google-sheets';
import { getFinanceTrackerTitle } from '../constants/common';
import { getCurrencyByCode } from '../utils/currency';

const ExpenseForm = ({ selectedYear, categories, setExpenses, currency = 'USD', showCurrency = true, showAmounts = true }) => {
  const currentCurrency = getCurrencyByCode(currency);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    amount: '',
    category: '',
    subCategory: '',
    additionalInfo: '',
    date: new Date().toISOString().split('T')[0] // Default to today
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isInitializing, setIsInitializing] = useState(false);
  const [spreadsheetId, setSpreadsheetId] = useState(null);
  const [initError, setInitError] = useState(null);
  const [availableSubCategories, setAvailableSubCategories] = useState([]);
  const { currentUser } = useUser();

  // Spreadsheet title based on selected year
  const SPREADSHEET_TITLE = getFinanceTrackerTitle(selectedYear);

  // Get unique categories from the categories array
  const uniqueCategories = [...new Set(categories.map(cat => cat.category))];

  // Update available subcategories when category changes
  useEffect(() => {
    if (formData.category) {
      const subCats = categories
        .filter(cat => cat.category === formData.category)
        .map(cat => cat.subCategory);
      setAvailableSubCategories(subCats);
    } else {
      setAvailableSubCategories([]);
    }
  }, [formData.category, categories]);

  // Initialize or get spreadsheet
  useEffect(() => {
    if (currentUser?.accessToken && selectedYear) {
      googleSheetsService.setAccessToken(currentUser.accessToken);
      initializeSpreadsheet();
    }
  }, [currentUser?.accessToken, selectedYear]);

  const initializeSpreadsheet = async () => {
    setIsInitializing(true);
    setInitError(null);

    try {
      const storedId = localStorage.getItem(`finance_sheet_${selectedYear}`);

      // First, try to use the stored ID
      if (storedId) {
        const isValid = await googleSheetsService.verifySpreadsheet(storedId);

        if (isValid) {
          setSpreadsheetId(storedId);
          setIsInitializing(false);
          return;
        } else {
          localStorage.removeItem(`finance_sheet_${selectedYear}`);
        }
      }

      // If no valid stored ID, search by name using Google Drive API
      const found = await googleSheetsService.searchSpreadsheetByName(SPREADSHEET_TITLE);

      if (found && found.spreadsheetId) {
        // Found existing sheet by name
        setSpreadsheetId(found.spreadsheetId);
        localStorage.setItem(`finance_sheet_${selectedYear}`, found.spreadsheetId);
        setIsInitializing(false);
        return;
      }

      // If not found by name either, create a new spreadsheet
      await createNewSpreadsheet();
    } catch (error) {
      console.error('Error initializing spreadsheet:', error);
      setInitError(error.message);
      setIsInitializing(false);
    }
  };

  const createNewSpreadsheet = async () => {
    try {
      const { spreadsheetId: newSpreadsheetId } = await googleSheetsService.createSpreadsheet(
        SPREADSHEET_TITLE
      );

      setSpreadsheetId(newSpreadsheetId);
      localStorage.setItem(`finance_sheet_${selectedYear}`, newSpreadsheetId);

      const headers = [
        'Date',
        'Amount',
        'Category',
        'Sub-Category',
        'Additional Info',
        'Month',
        'Year'
      ];
      await googleSheetsService.addHeaders(newSpreadsheetId, headers);

      setIsInitializing(false);
    } catch (error) {
      console.error('Error creating spreadsheet:', error);
      setInitError(error.message);
      setIsInitializing(false);
      throw error;
    }
  };

  const addExpenseToSheet = async (expense) => {
    try {
      setIsSubmitting(true);

      // Determine the year from the expense date
      const expenseYear = expense.year;
      let targetSpreadsheetId = spreadsheetId;

      // If expense year is different from selectedYear, find/create the appropriate sheet
      if (expenseYear !== selectedYear) {
        const targetSheetTitle = getFinanceTrackerTitle(expenseYear);
        const storedId = localStorage.getItem(`finance_sheet_${expenseYear}`);

        if (storedId) {
          const isValid = await googleSheetsService.verifySpreadsheet(storedId);
          if (isValid) {
            targetSpreadsheetId = storedId;
          } else {
            localStorage.removeItem(`finance_sheet_${expenseYear}`);
          }
        }

        // If no valid stored ID, search by name
        if (!targetSpreadsheetId || targetSpreadsheetId === spreadsheetId) {
          const found = await googleSheetsService.searchSpreadsheetByName(targetSheetTitle);

          if (found && found.spreadsheetId) {
            targetSpreadsheetId = found.spreadsheetId;
            localStorage.setItem(`finance_sheet_${expenseYear}`, found.spreadsheetId);
          } else {
            // Create new spreadsheet for this year
            const { spreadsheetId: newSpreadsheetId } = await googleSheetsService.createSpreadsheet(targetSheetTitle);
            targetSpreadsheetId = newSpreadsheetId;
            localStorage.setItem(`finance_sheet_${expenseYear}`, newSpreadsheetId);

            const headers = [
              'Date',
              'Amount',
              'Category',
              'Sub-Category',
              'Additional Info',
              'Month',
              'Year'
            ];
            await googleSheetsService.addHeaders(newSpreadsheetId, headers);
          }
        }
      }

      // If still no spreadsheet ID (for current year), initialize it
      if (!targetSpreadsheetId) {
        await initializeSpreadsheet();
        await new Promise(resolve => setTimeout(resolve, 500));

        if (!spreadsheetId) {
          alert('Failed to initialize spreadsheet. Please try again.');
          return false;
        }
        targetSpreadsheetId = spreadsheetId;
      }

      const expenseDate = new Date(expense.date);
      const values = [
        expense.date,
        expense.amount,
        expense.category,
        expense.subCategory,
        expense.additionalInfo,
        expenseDate.toLocaleString('default', { month: 'long' }),
        expense.year,
      ];

      await googleSheetsService.appendRow(targetSpreadsheetId, values);

      return true;
    } catch (error) {
      console.error('Error adding expense:', error);
      alert(`Failed to add expense: ${error.message}`);
      return false;
    } finally {
      setIsSubmitting(false);
    }
  };

  const validateForm = () => {
    const amountField = document.querySelector('input[name="amount"]');
    const categoryField = document.querySelector('select[name="category"]');
    const subCategoryField = document.querySelector('select[name="subCategory"]');

    document.querySelectorAll('.validation-error').forEach(el => el.remove());
    [amountField, categoryField, subCategoryField].forEach(field => {
      if (field) {
        field.style.borderColor = '#d1d5db';
        field.style.borderWidth = '1px';
      }
    });

    let hasErrors = false;

    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      if (amountField) {
        amountField.style.borderColor = '#ef4444';
        amountField.style.borderWidth = '2px';
        const errorDiv = document.createElement('div');
        errorDiv.className = 'validation-error text-red-500 text-sm mt-1';
        errorDiv.textContent = 'Amount is required and must be greater than 0';
        amountField.parentNode.appendChild(errorDiv);
      }
      hasErrors = true;
    }

    if (!formData.category) {
      if (categoryField) {
        categoryField.style.borderColor = '#ef4444';
        categoryField.style.borderWidth = '2px';
        const errorDiv = document.createElement('div');
        errorDiv.className = 'validation-error text-red-500 text-sm mt-1';
        errorDiv.textContent = 'Category is required';
        categoryField.parentNode.appendChild(errorDiv);
      }
      hasErrors = true;
    }

    if (!formData.subCategory) {
      if (subCategoryField) {
        subCategoryField.style.borderColor = '#ef4444';
        subCategoryField.style.borderWidth = '2px';
        const errorDiv = document.createElement('div');
        errorDiv.className = 'validation-error text-red-500 text-sm mt-1';
        errorDiv.textContent = 'Sub-Category is required';
        subCategoryField.parentNode.appendChild(errorDiv);
      }
      hasErrors = true;
    }

    return !hasErrors;
  };

  const handleAddExpense = async () => {
    if (!validateForm()) return;

    const expenseDate = new Date(formData.date);
    const newExpense = {
      id: Date.now().toString(),
      amount: parseFloat(formData.amount),
      category: formData.category,
      subCategory: formData.subCategory,
      additionalInfo: formData.additionalInfo.trim(),
      date: formData.date,
      month: expenseDate.getMonth(),
      year: expenseDate.getFullYear()
    };

    const success = await addExpenseToSheet(newExpense);

    if (success) {
      // Update local state to immediately reflect the new expense
      setExpenses(prev => [...prev, newExpense]);

      setFormData({ amount: '', category: '', subCategory: '', additionalInfo: '', date: new Date().toISOString().split('T')[0] });

      document.querySelectorAll('.validation-error').forEach(el => el.remove());
      const fields = document.querySelectorAll('input[name="amount"], select[name="category"], select[name="subCategory"]');
      fields.forEach(field => {
        if (field) {
          field.style.borderColor = '#d1d5db';
          field.style.borderWidth = '1px';
        }
      });
    }
  };

  const shouldShowNote = () => {
    const { category, subCategory } = formData;
    return (
      (category === 'HOUSING 🏠' && subCategory === 'Pest Control / Fertilizers / Salt Pellets / Hardware Tools') ||
      (category === 'FOOD 🥗' && subCategory === 'Restaurants 🍽️') ||
      (category === 'TRANSPORT 🚙' && subCategory === 'Car Insurance / Service / Oil change / Parking / Registration / Toll 🛢️ 🚙') ||
      (category === 'HOME ACCESSORIES 🖼️' && (
        subCategory === 'Kitchen Electronics / Accessories 👩🏻‍🍳' ||
        subCategory === 'General Electronics / Accessories 🕹️' ||
        subCategory === 'Upgrade Home Accessories / Labour / Material'
      )) ||
      (category === 'CHILDCARE' && subCategory === 'Baby Gear (Diaper, Car Seat, Stroller, etc.)') ||
      (category === 'MISCELLANEOUS' && (subCategory === 'Number 1' || subCategory === 'Gift 🎁'))
    );
  };

  const handleCancel = () => {
    setShowForm(false);
    setFormData({ amount: '', category: '', subCategory: '', additionalInfo: '', date: new Date().toISOString().split('T')[0] });

    document.querySelectorAll('.validation-error').forEach(el => el.remove());
    const fields = document.querySelectorAll('input[name="amount"], select[name="category"], select[name="subCategory"]');
    fields.forEach(field => {
      if (field) {
        field.style.borderColor = '#d1d5db';
        field.style.borderWidth = '1px';
      }
    });
  };

  // Check if categories are loaded
  const noCategoriesAvailable = categories.length === 0;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <button
          onClick={() => setShowForm(!showForm)}
          disabled={noCategoriesAvailable}
          className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors font-medium shadow-md hover:shadow-lg disabled:bg-gray-400 disabled:cursor-not-allowed"
        >
          {showForm ? 'Hide Form' : '+ Add New Expense'}
        </button>

        {spreadsheetId && !isInitializing && (
          <a
            href={`https://docs.google.com/spreadsheets/d/${spreadsheetId}`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1.5 text-green-600 hover:text-green-700 font-medium transition-colors"
            title="Connected to Google Sheets"
          >
            <svg
              className="w-5 h-5"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                clipRule="evenodd"
              />
            </svg>
            <span className="underline">sheet</span>
          </a>
        )}
      </div>

      {noCategoriesAvailable && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
          <p className="text-sm text-yellow-700">
            <strong>⚠️ No categories available:</strong> Please add categories in the Categories page before adding expenses.
          </p>
        </div>
      )}

      {isInitializing && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
          <p className="text-sm text-yellow-700">
            <strong>Initializing spreadsheet...</strong> Please wait.
          </p>
        </div>
      )}

      {initError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3">
          <p className="text-sm text-red-700">
            <strong>Error:</strong> {initError}
            <button
              onClick={initializeSpreadsheet}
              className="ml-2 underline hover:text-red-900 font-medium"
            >
              Retry
            </button>
          </p>
        </div>
      )}

      {showForm && !noCategoriesAvailable && (
        <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
          <h2 className="text-2xl font-semibold text-gray-800 mb-4">Add New Expense</h2>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Amount ({showCurrency ? (currentCurrency.symbol || currentCurrency.code) : ''}) <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  name="amount"
                  value={formData.amount}
                  onChange={(e) => setFormData(prev => ({ ...prev, amount: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  placeholder="0.00"
                  step="0.01"
                  min="0.01"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Category <span className="text-red-500">*</span>
                </label>
                <select
                  name="category"
                  value={formData.category}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    category: e.target.value,
                    subCategory: '',
                    additionalInfo: ''
                  }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                >
                  <option value="">Select Category</option>
                  {uniqueCategories.map(category => (
                    <option key={category} value={category}>{category}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Sub-Category <span className="text-red-500">*</span>
                </label>
                <select
                  name="subCategory"
                  value={formData.subCategory}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    subCategory: e.target.value,
                    additionalInfo: ''
                  }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all disabled:bg-gray-100 disabled:cursor-not-allowed"
                  disabled={!formData.category}
                >
                  <option value="">Select Sub-Category</option>
                  {availableSubCategories.map(subCat => (
                    <option key={subCat} value={subCat}>{subCat}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  name="date"
                  value={formData.date}
                  onChange={(e) => setFormData(prev => ({ ...prev, date: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Additional Info (Optional)
                </label>
                <input
                  type="text"
                  value={formData.additionalInfo}
                  onChange={(e) => setFormData(prev => ({ ...prev, additionalInfo: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  placeholder="Enter additional information or custom name"
                />
              </div>
            </div>

            {shouldShowNote() && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                <p className="text-sm text-yellow-700">
                  <strong>💡 Note:</strong> Additional info is recommended for this category to better track your expenses.
                </p>
              </div>
            )}

            <div className="flex gap-3 pt-4">
              <button
                onClick={handleAddExpense}
                disabled={isSubmitting || isInitializing}
                className="px-6 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed font-medium shadow-md hover:shadow-lg"
              >
                {isSubmitting ? 'Adding...' : isInitializing ? 'Initializing...' : 'Add Expense'}
              </button>
              <button
                onClick={handleCancel}
                disabled={isSubmitting}
                className="px-6 py-2 bg-gray-300 hover:bg-gray-400 text-gray-700 rounded-lg transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExpenseForm;
